import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSliderModule } from 'ng-zorro-antd/slider';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTreeModule } from 'ng-zorro-antd/tree';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FikoSPHElemanPanelComponent } from './fiko-sph-eleman-panel.component';
import { FikoSPHTimelineComponent } from './fiko-sph-timeline.component';
import { FikoSPHAnalizPanelComponent } from './fiko-sph-analiz-panel.component';
import { FikoSPHMotor, SimulasyonDurumu, SolverTipi, KaliteSeviyesi, RenderModu } from '../FikoSPHMotor';
import { ElemanTipi } from '../elemanlar/ISPHEleman';

@Component({
  selector: 'fiko-sph-sivi',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzSelectModule,
    NzInputNumberModule,
    NzSliderModule,
    NzSwitchModule,
    NzTabsModule,
    NzCardModule,
    NzTagModule,
    NzDividerModule,
    NzRadioModule,
    NzSkeletonModule,
    NzModalModule,
    NzDropdownModule,
    NzMenuModule,
    NzFormModule,
    NzGridModule,
    NzBadgeModule,
    NzIconModule,
    NzTreeModule,
    NzInputModule,
    FikoSPHElemanPanelComponent,
    FikoSPHTimelineComponent,
    FikoSPHAnalizPanelComponent
  ],
  templateUrl: './fiko-sph-sivi.component.html',
  styleUrls: ['./fiko-sph-sivi.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FikoSPHSiviComponent implements OnInit, OnDestroy, AfterViewInit {
  // Dialog entegrasyonu - DialogFiko tarafindan set edilir
  private _pencere: any;
  private _anaNesne: any;

  @Input() set anaGosterim(val: any) {
    this._anaNesne = val;
    setTimeout(() => this.baslatMotor(), 100);
  }
  get anaGosterim() { return this._anaNesne; }

  @Input() set pencere(v: any) {
    this._pencere = v?.instance || v;
  }
  get pencere() { return this._pencere; }

  @Input() set anaNesne(val: any) {
    this._anaNesne = val;
    setTimeout(() => this.baslatMotor(), 100);
  }
  get anaNesne() { return this._anaNesne; }

  // WebGPU canvas reference
  @ViewChild('sphCanvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;

  // SPH Motor instance
  private motor: FikoSPHMotor | null = null;
  private motorHazir: boolean = false;
  private statsIntervalId: any = null;

  // UI state
  durum: string = 'bosta'; // bosta, hazirlaniyor, calisiyor, duraklatildi, tamamlandi, hata
  solverTipi: string = 'DFSPH';
  kalite: string = 'orta';
  simHizi: number = 1.0;
  renderModu: number = 0; // 0=particle, 1=fluid, 2=hibrit

  // Stats
  fps: number = 0;
  parcacikSayisi: number = 0;
  dolumYuzdesi: number = 0;
  simZaman: number = 0;
  webgpuDestekli: boolean = false;
  webgpuHata: string = '';
  yukleniyor: boolean = false;

  // FPS tracking
  private _sonFPSZaman: number = 0;
  private _sonFrameSayisi: number = 0;

  // Elements
  elemanKategorileri: any[] = [];
  elemanlar: any[] = [];
  seciliEleman: any = null;
  genisletilmisKategoriler: Set<string> = new Set();

  // Physics params (two-way bound)
  gravityX: number = 0;
  gravityY: number = -9.81;
  gravityZ: number = 0;
  viskozite: number = 0.001;
  yuzeyGerilimi: number = 0.0728;
  dinlenmeDensitesi: number = 1000;
  sinirSonumleme: number = 0.3;
  yaklasmaYaricapi: number = 0.02;

  // Heatmap
  heatmapAktif: boolean = false;
  heatmapTipi: number = 0;

  // Malzeme
  malzemeListesi: any[] = [
    { ad: 'su', etiket: 'Su', densitesi: 1000, viskozite: 0.001 },
    { ad: 'aliminyum', etiket: 'Aluminyum', densitesi: 2700, viskozite: 0.0012 },
    { ad: 'demir', etiket: 'Demir', densitesi: 7870, viskozite: 0.006 },
    { ad: 'bakir', etiket: 'Bakir', densitesi: 8960, viskozite: 0.004 },
    { ad: 'magnezyum', etiket: 'Magnezyum', densitesi: 1740, viskozite: 0.0011 }
  ];
  seciliMalzeme: string = 'su';

  // Drawer
  drawerGorunur: boolean = true;

  // Solver options
  solverSecenekleri = [
    { deger: 'DFSPH', etiket: 'DFSPH (Divergence-Free)' },
    { deger: 'PBF', etiket: 'PBF (Position-Based)' },
    { deger: 'PCISPH', etiket: 'PCISPH (Predictive-Corrective)' }
  ];

  // Quality options
  kaliteSecenekleri = [
    { deger: 'dusuk', etiket: 'Dusuk (50k parcacik)' },
    { deger: 'orta', etiket: 'Orta (200k parcacik)' },
    { deger: 'yuksek', etiket: 'Yuksek (500k parcacik)' },
    { deger: 'ultra', etiket: 'Ultra (1M+ parcacik)' }
  ];

  // Render mode options
  renderModuSecenekleri = [
    { deger: 0, etiket: 'Parcacik' },
    { deger: 1, etiket: 'Akiskan' },
    { deger: 2, etiket: 'Hibrit' }
  ];

  // Statistics for analysis panel
  istatistikler: any = {};
  sensorVerileri: any[] = [];

  constructor(
    private cd: ChangeDetectorRef,
    private message: NzMessageService
  ) {}

  ngOnInit(): void {
    this.elemanKategorileriOlustur();
    this.ornekElemanlariOlustur();
  }

  ngAfterViewInit(): void {
    // Canvas will be ready, but wait for anaNesne to trigger baslatMotor()
  }

  ngOnDestroy(): void {
    // Stop stats update interval
    if (this.statsIntervalId) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = null;
    }

    // Cleanup motor
    if (this.motor) {
      try {
        this.motor.temizle();
      } catch (e) {
        console.error('[FikoSPHSivi] Motor cleanup error:', e);
      }
      this.motor = null;
    }

    this.motorHazir = false;
  }

  /**
   * Initialize FikoSPHMotor with WebGPU canvas
   */
  private _baslatRetry = 0;
  private async baslatMotor(): Promise<void> {
    if (this.motorHazir) return;
    if (!this.canvasRef) {
      if (this._baslatRetry < 20) {
        this._baslatRetry++;
        setTimeout(() => this.baslatMotor(), 200);
      }
      return;
    }
    this._baslatRetry = 0;

    // Check WebGPU support
    if (!navigator.gpu) {
      this.webgpuDestekli = false;
      this.webgpuHata = 'WebGPU desteklenmiyor. Chrome 113+ veya Edge 113+ gereklidir.';
      this.durum = 'hata';
      this.yukleniyor = false;
      this.message.error('WebGPU bu tarayicida desteklenmiyor');
      this.cd.markForCheck();
      return;
    }

    this.webgpuDestekli = true;
    this.durum = 'hazirlaniyor';
    this.yukleniyor = true;
    this.cd.markForCheck();

    try {
      // Create motor instance with proper SPHSimulasyonConfig format
      this.motor = new FikoSPHMotor({
        global: {
          solverTipi: this.solverTipi as SolverTipi,
          gravity: { x: this.gravityX, y: this.gravityY, z: this.gravityZ },
          viscosity: this.viskozite,
          surfaceTension: this.yuzeyGerilimi,
          restDensity: this.dinlenmeDensitesi,
          boundaryDamping: this.sinirSonumleme,
          smoothingRadius: this.yaklasmaYaricapi,
          maxParticle: this.getMaxParcacikSayisi(),
          kalite: this.kalite as KaliteSeviyesi,
          simBounds: {
            min: { x: -0.15, y: -0.01, z: -0.15 },
            max: { x: 0.15, y: 0.30, z: 0.15 }
          }
        }
      } as any);

      // Setup motor event listeners
      this.setupMotorEvents();

      // Initialize motor with canvas
      const canvas = this.canvasRef.nativeElement;
      const baslaBasarili = await this.motor.basla(canvas);

      if (!baslaBasarili) {
        throw new Error('Motor baslatma basarisiz');
      }

      this.motorHazir = true;
      this.durum = 'bosta';
      this.yukleniyor = false;
      this.message.success('SPH Motor hazir');

      // Add a default inlet for testing
      this.varsayilanGirisEkle();

      // Start periodic stats update (1Hz)
      this.statsIntervalId = setInterval(() => {
        this.istatistikleriGuncelle();
      }, 1000);

      this.cd.markForCheck();
      console.log('[FikoSPHSivi] Motor basarili bir sekilde baslatildi');

    } catch (e: any) {
      console.error('[FikoSPHSivi] Motor baslatma hatasi:', e);
      this.webgpuHata = e.message || 'Motor baslatma hatasi';
      this.durum = 'hata';
      this.yukleniyor = false;
      this.message.error('Motor baslatma basarisiz: ' + this.webgpuHata);
      this.cd.markForCheck();
    }
  }

  /**
   * Setup motor event listeners
   */
  private setupMotorEvents(): void {
    if (!this.motor) return;

    this.motor.on('onDurumDegisti', (yeniDurum: SimulasyonDurumu) => {
      this.durum = yeniDurum;
      this.cd.markForCheck();
    });

    this.motor.on('onFrameTamamlandi', (istatistik: any) => {
      // istatistik: { frameCounter, aktifParcacikSayisi, simDt }
      if (istatistik?.aktifParcacikSayisi !== undefined) {
        this.parcacikSayisi = istatistik.aktifParcacikSayisi;
      }
    });

    this.motor.on('onHata', (hata: Error) => {
      console.error('[FikoSPHSivi] Motor hatasi:', hata);
      this.webgpuHata = hata.message;
      this.durum = 'hata';
      this.message.error('Simulasyon hatasi: ' + hata.message);
      this.cd.markForCheck();
    });

    this.motor.on('onSolverDegisti', (yeniSolver: SolverTipi) => {
      this.solverTipi = yeniSolver;
      console.log('[FikoSPHSivi] Solver degisti:', yeniSolver);
      this.cd.markForCheck();
    });

    this.motor.on('onElemanEklendi', (eleman: any) => {
      console.log('[FikoSPHSivi] Eleman eklendi:', eleman);
      this.cd.markForCheck();
    });

    this.motor.on('onElemanSilindi', (id: string) => {
      console.log('[FikoSPHSivi] Eleman silindi:', id);
      this.cd.markForCheck();
    });

    this.motor.on('onDolumTamamlandi', () => {
      this.dolumYuzdesi = 100;
      console.log('[FikoSPHSivi] Dolum tamamlandi');
      this.cd.markForCheck();
    });
  }

  /**
   * Update statistics from motor
   */
  private istatistikleriGuncelle(): void {
    if (!this.motor || !this.motorHazir) return;

    try {
      // Calculate FPS from frame count
      if (!this._sonFPSZaman) {
        this._sonFPSZaman = performance.now();
        this._sonFrameSayisi = 0;
      }
      const simdi = performance.now();
      const gecenSure = simdi - this._sonFPSZaman;
      if (gecenSure >= 1000) {
        this.fps = Math.round((this._sonFrameSayisi / gecenSure) * 1000);
        this._sonFPSZaman = simdi;
        this._sonFrameSayisi = 0;
      }
      this._sonFrameSayisi++;

      const motorStats = this.motor.getIstatistikler();
      this.parcacikSayisi = this.motor.getAktifParcacikSayisi();
      this.simZaman = this.motor.getSimZaman();
      this.durum = this.motor.getDurum();

      this.istatistikler = {
        fps: this.fps,
        parcacikSayisi: this.parcacikSayisi,
        dolumYuzdesi: this.dolumYuzdesi,
        simZaman: this.simZaman,
        ...motorStats
      };

      // Update sensor data from motor stats
      this.sensorVerileri = motorStats?.sensorler || [];

      this.cd.markForCheck();
    } catch (e) {
      console.error('[FikoSPHSivi] Stats update error:', e);
    }
  }

  /**
   * Get max particle count based on quality setting
   */
  private getMaxParcacikSayisi(): number {
    const kaliteDeger: Record<string, number> = {
      'dusuk': 50000,
      'orta': 200000,
      'yuksek': 500000,
      'ultra': 1000000,
      'manuel': 200000
    };
    return kaliteDeger[this.kalite] || 200000;
  }

  /**
   * Map kategori ID to element type
   */
  private elemanTipEsle(kategoriId: string): ElemanTipi | null {
    const eslesme: Record<string, ElemanTipi> = {
      'dokum-girisleri': ElemanTipi.DOKUM_GIRISI,
      'hava-delikleri': ElemanTipi.HAVA_DELIGI,
      'engeller': ElemanTipi.ENGEL,
      'filtreler': ElemanTipi.FILTRE,
      'besleyiciler': ElemanTipi.BESLEYICI,
      'cikislar': ElemanTipi.CIKIS,
      'sensorler': ElemanTipi.SANAL_SENSOR,
      'termal': ElemanTipi.TERMAL_ELEMAN
    };
    return eslesme[kategoriId] || null;
  }

  private elemanKategorileriOlustur(): void {
    this.elemanKategorileri = [
      { id: 'dokum-girisleri', ad: 'Dokum Girisleri', ikon: 'arrow-down', elemanlar: [] },
      { id: 'hava-delikleri', ad: 'Hava Delikleri', ikon: 'api', elemanlar: [] },
      { id: 'engeller', ad: 'Engeller', ikon: 'border', elemanlar: [] },
      { id: 'filtreler', ad: 'Filtreler', ikon: 'filter', elemanlar: [] },
      { id: 'besleyiciler', ad: 'Besleyiciler', ikon: 'plus-circle', elemanlar: [] },
      { id: 'cikislar', ad: 'Cikislar', ikon: 'export', elemanlar: [] },
      { id: 'sensorler', ad: 'Sensorler', ikon: 'experiment', elemanlar: [] },
      { id: 'termal', ad: 'Termal', ikon: 'fire', elemanlar: [] }
    ];
  }

  private ornekElemanlariOlustur(): void {
    // Add some example elements
    this.elemanlar = [
      { id: '1', ad: 'Giris 1', tip: 'dokum-girisleri', aktif: true, parametreler: {} },
      { id: '2', ad: 'Hava Deligi 1', tip: 'hava-delikleri', aktif: true, parametreler: {} }
    ];
    this.elemanlariKategorilereAta();
  }

  private elemanlariKategorilereAta(): void {
    this.elemanKategorileri.forEach(kat => kat.elemanlar = []);
    this.elemanlar.forEach(eleman => {
      const kategori = this.elemanKategorileri.find(k => k.id === eleman.tip);
      if (kategori) {
        kategori.elemanlar.push(eleman);
      }
    });
  }

  // Actions
  baslat(): void {
    if (!this.motorHazir || !this.motor) {
      this.message.warning('Motor henuz hazir degil');
      return;
    }

    try {
      // Only reset simulation state, not elements
      if (this.durum === 'tamamlandi') {
        this.motor.sifirla();
      }
      this.motor.baslat();
      this.durum = 'calisiyor';
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Baslatma hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Baslatma hatasi:', e);
    }
  }

  duraklat(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      if (this.durum === 'calisiyor') {
        this.motor.duraklat();
        this.durum = 'duraklatildi';
      } else if (this.durum === 'duraklatildi') {
        this.motor.devamEt();
        this.durum = 'calisiyor';
      }
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Duraklat hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Duraklat hatasi:', e);
    }
  }

  durdur(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.durdur();
      this.durum = 'bosta';
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Durdur hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Durdur hatasi:', e);
    }
  }

  sifirla(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.sifirla();
      this.durum = 'bosta';
      this.simZaman = 0;
      this.dolumYuzdesi = 0;
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Sifirlama hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Sifirlama hatasi:', e);
    }
  }

  async solverDegistir(): Promise<void> {
    if (!this.motorHazir || !this.motor) return;

    try {
      await this.motor.solverDegistir(this.solverTipi as SolverTipi);
      this.message.success('Solver degistirildi: ' + this.solverTipi);
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Solver degistirme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Solver degistirme hatasi:', e);
    }
  }

  kaliteDegistir(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.kaliteAyarla(this.kalite as KaliteSeviyesi);
      const yeniParcacikSayisi = this.getMaxParcacikSayisi();
      this.parcacikSayisi = yeniParcacikSayisi;
      this.message.success('Kalite degistirildi: ' + this.kalite);
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Kalite degistirme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Kalite degistirme hatasi:', e);
    }
  }

  hizDegistir(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.simHiziAyarla(this.simHizi);
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Hiz degistirme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Hiz degistirme hatasi:', e);
    }
  }

  // Elements
  kategoriGeniletKapat(kategoriId: string): void {
    if (this.genisletilmisKategoriler.has(kategoriId)) {
      this.genisletilmisKategoriler.delete(kategoriId);
    } else {
      this.genisletilmisKategoriler.add(kategoriId);
    }
    this.cd.markForCheck();
  }

  kategoriGenisletildiMi(kategoriId: string): boolean {
    return this.genisletilmisKategoriler.has(kategoriId);
  }

  elemanEkle(kategoriId: string): void {
    if (!this.motorHazir || !this.motor) {
      this.message.warning('Motor henuz hazir degil');
      return;
    }

    const kategori = this.elemanKategorileri.find(k => k.id === kategoriId);
    if (!kategori) return;

    try {
      const parametreler = this.varsayilanParametreleriAl(kategoriId);
      let motorId: string = '';

      // Call appropriate motor method based on category
      switch (kategoriId) {
        case 'dokum-girisleri':
          motorId = this.motor.dokumGirisiEkle(parametreler);
          break;
        case 'hava-delikleri':
          motorId = this.motor.havaDeligiEkle(parametreler);
          break;
        case 'engeller':
          motorId = this.motor.engelEkle(parametreler);
          break;
        case 'filtreler':
          motorId = this.motor.filtreEkle(parametreler);
          break;
        case 'besleyiciler':
          motorId = this.motor.besleyiciEkle(parametreler);
          break;
        case 'cikislar':
          motorId = this.motor.cikisEkle(parametreler);
          break;
        case 'sensorler':
          motorId = this.motor.sensorEkle(parametreler);
          break;
        case 'termal':
          motorId = this.motor.termalElemanEkle(parametreler);
          break;
        default:
          this.message.warning('Bilinmeyen eleman tipi: ' + kategoriId);
          return;
      }

      // Add to local element list
      const yeniEleman = {
        id: motorId,
        ad: `${kategori.ad} ${kategori.elemanlar.length + 1}`,
        tip: kategoriId,
        aktif: true,
        parametreler: parametreler
      };

      this.elemanlar.push(yeniEleman);
      this.elemanlariKategorilereAta();
      this.seciliEleman = yeniEleman;
      this.message.success('Eleman eklendi: ' + yeniEleman.ad);
      this.cd.markForCheck();

    } catch (e: any) {
      this.message.error('Eleman ekleme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Eleman ekleme hatasi:', e);
    }
  }

  private varsayilanParametreleriAl(tip: string): any {
    const varsayilanlar: any = {
      'dokum-girisleri': {
        pozisyonX: 0, pozisyonY: 1, pozisyonZ: 0,
        yonX: 0, yonY: -1, yonZ: 0,
        hiz: 1.0, akisHizi: 0.5, yaricap: 0.1,
        kesitTipi: 'daire', sicaklik: 20, turbulans: 0.1,
        akisProfili: 'uniform', baslamaZamani: 0, bitisZamani: 10
      },
      'hava-delikleri': {
        pozisyonX: 0, pozisyonY: 0, pozisyonZ: 0,
        yonX: 0, yonY: 1, yonZ: 0,
        cap: 0.05, derinlik: 0.2, geriBasinc: 0,
        direncs: 0.5, gecirgenlik: 0.8, tip: 'standart'
      },
      'engeller': {
        pozisyonX: 0, pozisyonY: 0.5, pozisyonZ: 0,
        yonX: 0, yonY: 0, yonZ: 1,
        genislik: 1, yukseklik: 1, derinlik: 0.1,
        kalinlik: 0.05, gecirgenlik: 0, sürüklenme: 0.5,
        hareketTipi: 'sabit', donusHizi: 0
      },
      'filtreler': {
        pozisyonX: 0, pozisyonY: 0, pozisyonZ: 0,
        genislik: 0.5, yukseklik: 0.5, kalinlik: 0.1,
        gecirgenlik: 0.5, direnc: 0.3
      },
      'besleyiciler': {
        pozisyonX: 0, pozisyonY: 0.5, pozisyonZ: 0,
        akisHizi: 0.3, yaricap: 0.05, baslamaZamani: 0
      },
      'cikislar': {
        pozisyonX: 0, pozisyonY: -0.5, pozisyonZ: 0,
        yaricap: 0.1, basincFarki: 0
      },
      'sensorler': {
        pozisyonX: 0, pozisyonY: 0, pozisyonZ: 0,
        tip: 'basinc', yaricap: 0.05
      },
      'termal': {
        pozisyonX: 0, pozisyonY: 0, pozisyonZ: 0,
        sicaklik: 100, guc: 50, yaricap: 0.1
      }
    };

    return varsayilanlar[tip] || {};
  }

  elemanSec(eleman: any): void {
    this.seciliEleman = eleman;
    this.cd.markForCheck();
  }

  elemanSil(id: string): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      // Remove from motor
      this.motor.elemanSil(id);

      // Remove from local list
      this.elemanlar = this.elemanlar.filter(e => e.id !== id);
      this.elemanlariKategorilereAta();

      if (this.seciliEleman?.id === id) {
        this.seciliEleman = null;
      }

      this.message.success('Eleman silindi');
      this.cd.markForCheck();

    } catch (e: any) {
      this.message.error('Eleman silme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Eleman silme hatasi:', e);
    }
  }

  elemanDegisti(eleman: any): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      const index = this.elemanlar.findIndex(e => e.id === eleman.id);
      if (index !== -1) {
        this.elemanlar[index] = eleman;
        this.elemanlariKategorilereAta();

        // Update motor element parameters
        for (const [key, value] of Object.entries(eleman.parametreler)) {
          this.motor.elemanGuncelle(eleman.id, key, value);
        }

        this.cd.markForCheck();
      }
    } catch (e: any) {
      this.message.error('Eleman guncelleme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Eleman guncelleme hatasi:', e);
    }
  }

  elemanSilindi(id: string): void {
    this.elemanSil(id);
  }

  // Params
  fizikGuncelle(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.configGuncelle({
        global: {
          gravity: { x: this.gravityX, y: this.gravityY, z: this.gravityZ },
          viscosity: this.viskozite,
          surfaceTension: this.yuzeyGerilimi,
          restDensity: this.dinlenmeDensitesi,
          boundaryDamping: this.sinirSonumleme,
          smoothingRadius: this.yaklasmaYaricapi
        }
      } as any);

      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Fizik guncelleme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Fizik guncelleme hatasi:', e);
    }
  }

  renderModuDegistir(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.renderModuAyarla(this.renderModu as any);
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Render modu degistirme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Render modu degistirme hatasi:', e);
    }
  }

  malzemeDegistir(): void {
    const malzeme = this.malzemeListesi.find(m => m.ad === this.seciliMalzeme);
    if (malzeme) {
      this.dinlenmeDensitesi = malzeme.densitesi;
      this.viskozite = malzeme.viskozite;
      this.fizikGuncelle();
    }
    this.cd.markForCheck();
  }

  heatmapDegistir(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.heatmapAyarla(this.heatmapTipi, this.heatmapAktif);
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Heatmap degistirme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Heatmap degistirme hatasi:', e);
    }
  }

  heatmapSecildiHandler(tip: number): void {
    this.heatmapTipi = tip;
    this.heatmapAktif = true;
    this.heatmapDegistir();
  }

  hizDegistiHandler(yeniHiz: number): void {
    this.simHizi = yeniHiz;
    this.hizDegistir();
  }

  senaryoSecildiHandler(senaryo: any): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      this.motor.senaryoYukle(senaryo);
      this.message.success('Senaryo yuklendi');
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Senaryo yukleme hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Senaryo yukleme hatasi:', e);
    }
  }

  raporOlusturHandler(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      const rapor = this.motor.getIstatistikler();
      console.log('[FikoSPHSivi] Rapor olusturuluyor:', rapor);
      this.message.success('Rapor olusturuldu');
      this.cd.markForCheck();
    } catch (e: any) {
      this.message.error('Rapor olusturma hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Rapor olusturma hatasi:', e);
    }
  }

  // Export/Import
  exportJSON(): void {
    if (!this.motorHazir || !this.motor) return;

    try {
      const json = this.motor.exportJSON();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sph-simulasyon-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      this.message.success('Simulasyon export edildi');
    } catch (e: any) {
      this.message.error('Export hatasi: ' + e.message);
      console.error('[FikoSPHSivi] Export hatasi:', e);
    }
  }

  importJSON(event: any): void {
    if (!this.motorHazir || !this.motor) return;

    const file = event.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const json = e.target.result;
        this.motor!.importJSON(json);
        this.message.success('Simulasyon import edildi');
        this.cd.markForCheck();
      } catch (err: any) {
        this.message.error('Import hatasi: ' + err.message);
        console.error('[FikoSPHSivi] Import hatasi:', err);
      }
    };
    reader.readAsText(file);
  }

  /**
   * Add a default inlet element to the motor for initial testing
   */
  private varsayilanGirisEkle(): void {
    if (!this.motor) return;

    try {
      const girisId = this.motor.dokumGirisiEkle({
        pozisyon: { x: 0, y: 0.8, z: 0 },
        yon: { x: 0, y: -1, z: 0 },
        hiz: 2.0,
        debi: 800,
        yaricap: 0.08,
        aktif: true,
        ad: 'Varsayilan Giris'
      });

      // Add to UI element list
      const yeniEleman = {
        id: girisId,
        ad: 'Varsayilan Giris',
        tip: 'dokum-girisleri',
        aktif: true,
        parametreler: {}
      };
      this.elemanlar.push(yeniEleman);
      this.elemanlariKategorilereAta();

      console.log('[FikoSPHSivi] Varsayilan giris eklendi:', girisId);
    } catch (e: any) {
      console.warn('[FikoSPHSivi] Varsayilan giris eklenemedi:', e.message);
    }
  }
}
